import {
    CellDatetime,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { useIntl } from 'react-intl';
import useSearch from 'src/registry/hooks/useSearch';
import { createColumnHelper } from '@tanstack/react-table';
import useFilters from 'src/registry/hooks/useFilters';
import { useParams } from 'react-router-dom';
import CellSubscriptionItemUsageRecordActions from 'src/features/subscriptionItemUsageRecords/components/tables/CellSubscriptionItemUsageRecordActions';
import CreateSubscriptionItemUsageRecordFormModal from 'src/features/subscriptionItemUsageRecords/components/forms/CreateSubscriptionItemUsageRecordFormModal';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';
import { subItemQuantityEventMessages } from 'src/features/subscriptionItemQuantityEvents/utils/messages';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import React from 'react';
import { useListSubscriptionItemUsageRecord } from 'src/features/subscriptionItemUsageRecords/api/useListSubscriptionItemUsageRecord';
import { SubscriptionItemUsageRecord } from 'src/features/subscriptionItemUsageRecords/utils/types';
import { subItemUsageRecordMessages } from 'src/features/subscriptionItemUsageRecords/utils/messages';
import useSorting from 'src/registry/hooks/useSorting';

const SubscriptionItemUsageRecordTable = () => {
    const { formatMessage } = useIntl();
    const permissionManageSubscriptions = useHasPermissions([PERMISSION.MANAGE_SUBSCRIPTIONS]);
    const { subscriptionItemId = '' } = useParams();

    const [dateNow] = React.useState(() => Date.now());

    const { querySearchOptions, ...search } = useSearch();
    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'recorded_at', desc: false }],
    });

    const {
        data: usageRecordsData,
        isLoading,
        isError,
    } = useListSubscriptionItemUsageRecord({
        subscription_item: subscriptionItemId,
        ...querySearchOptions,
        ...queryFiltersOptions,
        ...querySortingOptions,
    });

    const columnHelper = createColumnHelper<SubscriptionItemUsageRecord>();

    const columns = [
        columnHelper.accessor('recorded_at', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            enableColumnFilter: true,
            enableSorting: true,
            meta: {
                title: formatMessage(subItemQuantityEventMessages.recordedAt),
                inputType: INPUT_TYPE.DATE,
                dateFilter: {
                    afterDate: new Date(dateNow - 30 * 24 * 60 * 60 * 1000),
                    beforeDate: new Date(),
                },
                inlineDateFilter: true,
            },
        }),
        columnHelper.accessor('billing_period_start', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            enableSorting: true,
            meta: {
                title: formatMessage(subItemUsageRecordMessages.billingPeriodStart),
            },
        }),
        columnHelper.accessor('billing_period_end', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            enableSorting: true,
            meta: {
                title: formatMessage(subItemUsageRecordMessages.billingPeriodEnd),
            },
        }),
        columnHelper.accessor('quantity', {
            header: DataTableColumnHeader,
            enableSorting: true,
            meta: {
                title: formatMessage(subItemQuantityEventMessages.quantity),
            },
        }),
        ...(permissionManageSubscriptions
            ? [
                  columnHelper.display({
                      id: 'actions',
                      header: DataTableColumnHeader,
                      cell: CellSubscriptionItemUsageRecordActions,
                      maxSize: 45,
                  }),
              ]
            : []),
    ];

    return (
        <DataTable<SubscriptionItemUsageRecord, unknown>
            columns={columns}
            data={usageRecordsData}
            semantics={{
                title: formatMessage(subItemUsageRecordMessages.usageRecords),
                entryName: formatMessage(subItemUsageRecordMessages.record).toLowerCase(),
                entryNames: formatMessage(subItemUsageRecordMessages.records).toLowerCase(),
                placeholder: formatMessage(subItemUsageRecordMessages.record).toLowerCase(),
            }}
            search={search}
            sorting={sorting}
            filters={filters}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(permissionManageSubscriptions && { newEntry: CreateSubscriptionItemUsageRecordFormModal }),
            }}
        />
    );
};

export default SubscriptionItemUsageRecordTable;
